<script setup lang="ts">
import { computed } from 'vue';
import { useAudioStore } from '../../stores/audioStore';

const audioStore = useAudioStore();

// Convert effect values to slider ranges
const pitchValue = computed({
  get: () => ((audioStore.effects.pitch + 12) / 24) * 100,
  set: (val: number) => audioStore.setPitch((val / 100) * 24 - 12),
});

const reverbValue = computed({
  get: () => audioStore.effects.reverb * 100,
  set: (val: number) => audioStore.setReverb(val / 100),
});

const volumeValue = computed({
  get: () => ((audioStore.effects.volume + 60) / 60) * 100,
  set: (val: number) => audioStore.setVolume((val / 100) * 60 - 60),
});

const progressPercent = computed(() => audioStore.progress * 100);
</script>

<template>
  <div class="h-14 bg-black/40 border-b border-console-border/30 px-4 md:px-8 flex items-center gap-6">
    <!-- Playback Controls -->
    <div class="flex items-center gap-4 min-w-[200px] md:min-w-[300px]">
      <button
        @click="audioStore.togglePlayPause()"
        class="size-9 flex items-center justify-center rounded-full bg-primary text-white shrink-0 hover:scale-110 transition-transform disabled:opacity-50"
        :disabled="!audioStore.currentClip"
      >
        <span class="material-symbols-outlined text-xl">
          {{ audioStore.isPlaying ? 'pause' : 'play_arrow' }}
        </span>
      </button>

      <div class="flex-1 min-w-0">
        <template v-if="audioStore.currentClip">
          <p class="text-xs font-bold truncate">
            {{ audioStore.currentTitle }}
            <span class="text-slate-500 font-normal"> ({{ audioStore.currentActor }})</span>
          </p>
          <div class="h-1 bg-slate-800 rounded-full mt-1 overflow-hidden">
            <div
              class="h-full bg-primary transition-all duration-100"
              :style="{ width: `${progressPercent}%` }"
            ></div>
          </div>
        </template>
        <template v-else>
          <p class="text-xs text-slate-500">No clip playing</p>
        </template>
      </div>
    </div>

    <!-- Effects Controls (Desktop) -->
    <div class="hidden md:flex flex-1 items-center justify-center gap-10">
      <!-- Pitch -->
      <div class="flex items-center gap-3">
        <span class="text-[10px] font-bold text-slate-500 uppercase">Pitch</span>
        <input
          type="range"
          :value="pitchValue"
          @input="pitchValue = Number(($event.target as HTMLInputElement).value)"
          min="0"
          max="100"
          class="w-32 h-1 bg-slate-800 rounded-lg appearance-none cursor-pointer accent-primary"
        />
      </div>

      <!-- Reverb -->
      <div class="flex items-center gap-3">
        <span class="text-[10px] font-bold text-slate-500 uppercase">Reverb</span>
        <input
          type="range"
          :value="reverbValue"
          @input="reverbValue = Number(($event.target as HTMLInputElement).value)"
          min="0"
          max="100"
          class="w-32 h-1 bg-slate-800 rounded-lg appearance-none cursor-pointer accent-primary"
        />
      </div>
    </div>

    <!-- Volume & Options -->
    <div class="flex items-center gap-4 ml-auto">
      <span class="material-symbols-outlined text-slate-400 text-xl cursor-pointer hover:text-white">
        volume_up
      </span>
      <input
        type="range"
        :value="volumeValue"
        @input="volumeValue = Number(($event.target as HTMLInputElement).value)"
        min="0"
        max="100"
        class="hidden sm:block w-24 h-1 bg-slate-800 rounded-lg appearance-none cursor-pointer accent-primary"
      />
      <button class="material-symbols-outlined text-slate-400 text-xl hover:text-primary">
        shuffle
      </button>
      <button class="material-symbols-outlined text-slate-400 text-xl hover:text-primary">
        loop
      </button>
    </div>
  </div>
</template>
